import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Import all available icons for selection
import * as LucideIcons from 'lucide-react';

interface LeadStatus {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string;
  display_order: number;
  is_default: boolean;
}

interface LeadStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  status?: LeadStatus | null;
}

export function LeadStatusModal({ isOpen, onClose, onSuccess, status }: LeadStatusModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [color, setColor] = useState('blue');
  const [icon, setIcon] = useState('User');
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Available colors
  const colors = [
    { name: 'Blue', value: 'blue' },
    { name: 'Green', value: 'green' },
    { name: 'Red', value: 'red' },
    { name: 'Yellow', value: 'yellow' },
    { name: 'Purple', value: 'purple' },
    { name: 'Pink', value: 'pink' },
    { name: 'Indigo', value: 'indigo' },
    { name: 'Gray', value: 'gray' }
  ];

  // Available icons - filter out only the icon components from lucide-react
  const iconNames = Object.keys(LucideIcons).filter(
    key => typeof LucideIcons[key as keyof typeof LucideIcons] === 'function' && 
    key !== 'createLucideIcon' && 
    !key.startsWith('create')
  );

  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (status) {
      setName(status.name);
      setSlug(status.slug);
      setColor(status.color);
      setIcon(status.icon);
      setIsDefault(status.is_default);
    } else {
      setName('');
      setSlug('');
      setColor('blue');
      setIcon('User');
      setIsDefault(false);
    }
  }, [status, isOpen]);

  const generateSlug = (input: string) => {
    return input
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    
    // Only auto-generate slug if it's a new status or if the slug hasn't been manually edited
    if (!status || slug === generateSlug(name)) {
      setSlug(generateSlug(newName));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!name.trim()) {
        throw new Error('Status name is required');
      }

      if (!slug.trim()) {
        throw new Error('Status slug is required');
      }

      if (!icon) {
        throw new Error('Status icon is required');
      }

      const { data, error } = await supabase.rpc('upsert_lead_status', {
        p_id: status?.id || null,
        p_name: name.trim(),
        p_slug: slug.trim(),
        p_color: color,
        p_icon: icon,
        p_display_order: status?.display_order || 999, // High number for new statuses
        p_is_default: isDefault
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving lead status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Render the selected icon
  const SelectedIcon = LucideIcons[icon as keyof typeof LucideIcons] as React.FC<{ className?: string }>;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {status ? 'Edit Lead Status' : 'Add New Lead Status'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="ml-3 text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Status Name
              </label>
              <input
                ref={nameInputRef}
                type="text"
                id="name"
                value={name}
                onChange={handleNameChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                placeholder="Enter status name"
                required
              />
            </div>

            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                Status Slug
              </label>
              <input
                type="text"
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                placeholder="enter_slug_name"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Used as an identifier in the database. Use lowercase letters, numbers, and underscores only.
              </p>
            </div>

            <div>
              <label htmlFor="color" className="block text-sm font-medium text-gray-700">
                Color
              </label>
              <div className="mt-1 grid grid-cols-4 gap-2">
                {colors.map((colorOption) => (
                  <button
                    key={colorOption.value}
                    type="button"
                    className={`h-10 rounded-md flex items-center justify-center ${
                      color === colorOption.value 
                        ? `ring-2 ring-offset-2 ring-${colorOption.value}-500 bg-${colorOption.value}-100` 
                        : `bg-${colorOption.value}-50 hover:bg-${colorOption.value}-100`
                    }`}
                    onClick={() => setColor(colorOption.value)}
                  >
                    <div className={`w-6 h-6 rounded-full bg-${colorOption.value}-500`}></div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="icon" className="block text-sm font-medium text-gray-700">
                Icon
              </label>
              <div className="mt-1 relative">
                <select
                  id="icon"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                >
                  {iconNames.map((iconName) => (
                    <option key={iconName} value={iconName}>
                      {iconName}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-8 pointer-events-none">
                  {SelectedIcon && <SelectedIcon className="h-5 w-5 text-gray-400" />}
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="is_default"
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
                Set as default status for new leads
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {status ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {status ? 'Update Status' : 'Create Status'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}